"use client"

import { useEffect, useMemo, useState } from "react"
import {
  Box, Button, Chip, Drawer, FormControl, IconButton, InputLabel,
  MenuItem, Select, Stack, TextField, Typography,
} from "@mui/material"
import CloseRoundedIcon from "@mui/icons-material/CloseRounded"
import { useNotification } from "@refinedev/core"
import {
  parseOrderNos,
  computeOrderNoDerived,
  normalizeOrderNoInput,
  type BucketArrays,
  type ParseResult,
} from "@/app/dashboard/operations/accounts/[id]/_lib/order-no-parse"
import { SubAccountAvatar, type SubAccount, subAccountLabel } from "@/app/dashboard/operations/accounts/_components/sub-account-avatar"
import { FormErrorChip } from "@/components/ui/form-dialog"
import { bjToday } from "@/lib/datetime"

// 全局浮窗「快录进单」抽屉。快路从慢路(「录入日数据」弹窗)提取:判断直接喂同款引擎 computeOrderNoDerived
// (缺时间/已录过/已成交/重复/位数不对 全口径一致)+ orderno-check 查重;文案照搬弹窗。主权 docs/todo/18。
// 进单是 OTA 漏斗专有(新媒体没有,见 business/07)→ 账号下拉只列 OTA。日期=今天;账号/子账号 sticky。

type QuickAccount = {
  id: string
  displayName: string
  companyName: string
  channelType: { category: string } | null
  subAccounts: SubAccount[] | null
}

const EMPTY: ParseResult = { ok: [], ignored: [], missingTime: 0 }
const LS_ACC = "bob.quickOrder.accountId"
const LS_SUB = "bob.quickOrder.subAccountId"

export function QuickOrderDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { open: notify } = useNotification()
  const [accounts, setAccounts] = useState<QuickAccount[]>([])
  const [accountId, setAccountId] = useState("")
  const [subAccountId, setSubAccountId] = useState("")
  const [ordersText, setOrdersText] = useState("")
  const [dbExisting, setDbExisting] = useState<{ orders: string[]; deals: string[] }>({ orders: [], deals: [] })
  const [shownBlock, setShownBlock] = useState<(string | false)[]>([])
  const [shownWarn, setShownWarn] = useState<(string | false)[]>([])
  const [submitting, setSubmitting] = useState(false)
  const date = bjToday()

  // 首次打开拉账号列表
  useEffect(() => {
    if (!open || accounts.length) return
    fetch("/api/operations/channel-accounts")
      .then((r) => r.json())
      .then((d) => setAccounts(Array.isArray(d?.accounts) ? d.accounts : []))
      .catch(() => setAccounts([]))
  }, [open, accounts.length])

  // sticky 回填(挂载一次,SSR 安全)
  useEffect(() => {
    const a = localStorage.getItem(LS_ACC)
    if (a) setAccountId(a)
    const s = localStorage.getItem(LS_SUB)
    if (s) setSubAccountId(s)
  }, [])

  const otaAccounts = useMemo(() => accounts.filter((a) => a.channelType?.category === "OTA"), [accounts])
  const account = useMemo(() => otaAccounts.find((a) => a.id === accountId) ?? null, [otaAccounts, accountId])
  const subs = useMemo(() => account?.subAccounts ?? [], [account])
  const hasSubs = subs.length > 0

  useEffect(() => {
    if (hasSubs && subAccountId && !subs.some((s) => s.id === subAccountId)) setSubAccountId("")
  }, [hasSubs, subs, subAccountId])

  // 解析 + 喂慢路引擎(单桶 _single)。引擎一把出:跳过/缺时间/重复/位数不对/已成交
  const orderParse = useMemo(() => parseOrderNos(ordersText, true, date), [ordersText, date])
  const hasOrders = orderParse.ok.length > 0
  const bucketArrays: BucketArrays = useMemo(
    () => ({ _single: { hand: EMPTY, order: orderParse, deal: EMPTY } }),
    [orderParse],
  )
  const derived = useMemo(
    () => computeOrderNoDerived(["_single"], bucketArrays, dbExisting),
    [bucketArrays, dbExisting],
  )

  // 查重(全局):停手 ~0.4s 后查库里已存在的 进单/成交 号 → 喂引擎判「已录过/已成交」
  useEffect(() => {
    const nos = orderParse.ok.map((o) => o.orderNo)
    if (!accountId || nos.length === 0) { setDbExisting({ orders: [], deals: [] }); return }
    const t = setTimeout(() => {
      fetch(`/api/operations/channel-accounts/${accountId}/orderno-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nos }),
      })
        .then((r) => r.json())
        .then((d) => setDbExisting({ orders: d.orders ?? [], deals: d.deals ?? [] }))
        .catch(() => {})
    }, 400)
    return () => clearTimeout(t)
  }, [orderParse, accountId])

  const pickAccount = (v: string) => { setAccountId(v); localStorage.setItem(LS_ACC, v) }
  const pickSub = (v: string) => { setSubAccountId(v); localStorage.setItem(LS_SUB, v) }

  // 判断/文案全照搬慢路:红=拦提交,黄=提醒不拦(已成交/已录过/重复/位数不对),乱字符静默
  const blockErrors = [
    derived.missingTimeCount.order > 0 && `进单还有 ${derived.missingTimeCount.order} 个订单号没写时间`,
    hasOrders && !accountId && "还要选渠道账号",
    hasOrders && hasSubs && !subAccountId && "还要选子账号",
  ]
  const warnErrors = [
    derived.orderSkippedDealt.size > 0 && `${derived.orderSkippedDealt.size} 个进单号已成交过,已自动跳过,请核对`,
    derived.orderSkippedOrdered.size > 0 && `${derived.orderSkippedOrdered.size} 个进单号之前已录过进单,已自动跳过,请核对`,
    derived.overlapCounts.order > 0 && `进单有 ${derived.overlapCounts.order} 个订单号重复(已自动只算一次)`,
    derived.malformedOrder.length > 0 && `进单号 ${derived.malformedOrder.join("、")} 格式不对,请核对`,
  ]
  const canSubmit =
    !!accountId && (!hasSubs || !!subAccountId) && derived.totals.order > 0 && derived.missingTimeCount.order === 0

  // 提示不实时:停手 ~0.4s 才定格(对齐慢路防抖快照),避免边打边在「位数不对↔缺时间」间闪跳
  useEffect(() => {
    const t = setTimeout(() => { setShownBlock(blockErrors); setShownWarn(warnErrors) }, 400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersText, accountId, subAccountId, hasSubs, dbExisting])

  const submit = async () => {
    if (!canSubmit || submitting) return
    // 入库订单:排除引擎判定要跳过的(已成交/已录过),同次粘贴 first-wins 去重
    const seen = new Set<string>()
    const records: { orderNo: string; occurredAt: string | null }[] = []
    orderParse.ok.forEach((e) => {
      if (derived.orderSkipped.has(e.orderNo) || seen.has(e.orderNo)) return
      seen.add(e.orderNo)
      records.push({ orderNo: e.orderNo, occurredAt: e.occurredAt })
    })
    setSubmitting(true)
    try {
      const res = await fetch(`/api/operations/channel-accounts/${accountId}/orderno-quick`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date, subAccountId: hasSubs ? subAccountId : null, orders: records }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error ?? "录入失败")
      notify?.({ type: "success", message: `已录入 ${data.added} 个进单`, description: "操作成功" })
      setOrdersText("") // 清单号,账号/子账号保留,接着录下一单
    } catch (e) {
      notify?.({ type: "error", message: "录入失败", description: (e as Error).message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: "100%", sm: 400 } } } }}
    >
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", height: "100%", gap: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>快录进单</Typography>
          <IconButton size="small" onClick={onClose}><CloseRoundedIcon fontSize="small" /></IconButton>
        </Stack>
        <Chip size="small" variant="outlined" label={`日期 · 今天 ${date}`} sx={{ alignSelf: "flex-start" }} />

        <FormControl size="small" fullWidth>
          <InputLabel>渠道账号</InputLabel>
          <Select label="渠道账号" value={accountId} onChange={(e) => pickAccount(e.target.value)}>
            {otaAccounts.map((a) => (
              <MenuItem key={a.id} value={a.id}>{a.displayName || a.companyName}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasSubs && (
          <FormControl size="small" fullWidth>
            <InputLabel>子账号</InputLabel>
            <Select label="子账号" value={subAccountId} onChange={(e) => pickSub(e.target.value)}>
              {subs.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SubAccountAvatar sub={s} size={24} />
                    <span>{subAccountLabel(s)}</span>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          label="进单订单号 + 时间"
          placeholder={"每行一个：订单号 时间\n例：1128145596001234 09:23"}
          multiline
          minRows={8}
          value={ordersText}
          onChange={(e) => setOrdersText(normalizeOrderNoInput(e.target.value))}
          fullWidth
          helperText={`已录 ${orderParse.ok.length} 单`}
          sx={{
            flexGrow: 1,
            "& .MuiInputBase-root": { height: "100%", alignItems: "flex-start" },
            "& .MuiInputBase-inputMultiline": { height: "100% !important", overflow: "auto" },
          }}
        />

        {/* 一次只定位一个问题、固定一个位置:红(拦)优先、否则黄(提醒);居左紧凑、opacity 渐显始终占位 → 不飘不跳 */}
        <Box sx={{ display: "flex" }}>
          <FormErrorChip
            errors={shownBlock.some(Boolean) ? shownBlock : shownWarn}
            color={shownBlock.some(Boolean) ? "error" : "warning"}
            maxWidth={344}
            tooltip={(shownBlock.some(Boolean) ? shownBlock : shownWarn).filter(Boolean).join(" · ")}
          />
        </Box>
        <Button variant="contained" disabled={!canSubmit || submitting} onClick={submit} fullWidth>
          {submitting ? "录入中…" : "录入"}
        </Button>
      </Box>
    </Drawer>
  )
}
